import { Box, Flex, FormControl, Input, Stack, Switch, Tag } from "@chakra-ui/react"
import { ErrorMessage, Formik } from "formik"
import { ComponentProps, FC, useState } from "react"
import ReactSelect from "react-select"
import { SelectOption } from "src/components/shared/ReactSelect"
import { District } from "src/domain/entities/district"
import { ITalukaUpdateFormFields } from "."
import { Taluka } from "../../../../domain/entities/taluka"
import { fetchGujaratiSuggestions } from "../../../../utils/helpers"
import { FormikOnSubmit } from "../../../../utils/types"
import { DrawerForm, ErrorMessageField } from "../../../ui"
import { InputLabel } from "../../../ui/InputLabel"

interface Props extends Omit<ComponentProps<typeof DrawerForm>, "children"> {
	taluka: Taluka
	handleSubmit: FormikOnSubmit<ITalukaUpdateFormFields>
	districtList: District[]
}

export const TalukaUpdateDrawerFormView: FC<Props> = ({
	taluka,
	handleSubmit,
	districtList,
	...rest
}) => {
	const [nameSuggestions, setNameSuggestions] = useState<string[]>([])

	return (
		<Formik<ITalukaUpdateFormFields>
			initialValues={{
				name: taluka.name,
				districtId: taluka.district?.id,
				sameAsParent: taluka.sameAsParent,
				isApproved: taluka.isApproved,
			}}
			onSubmit={handleSubmit}
			enableReinitialize={true}
		>
			{({ values, isSubmitting, handleChange, setFieldValue }) => {
				const handleNameChange = async (
					e: React.ChangeEvent<HTMLInputElement>,
				) => {
					setFieldValue("name.en", e.target.value)
					const suggestions = await fetchGujaratiSuggestions(e.target.value)
					setNameSuggestions(suggestions)
				}

				const districtOptions = districtList.map((district) => ({
					value: district.id,
					label: district.name.en + ` (${district.name.gu})`,
				}))

				return (
					<DrawerForm
						size="sm"
						headerLabel="Update Taluka"
						submitLabel="Save"
						isSubmitting={isSubmitting}
						{...rest}
					>
						<Stack maxWidth={"sm"} marginX={"auto"}>
							{/* District */}
							<FormControl flex={2}>
								<InputLabel label="District" />
								<ReactSelect
									name="districtId"
									onChange={(newValue) => {
										setFieldValue(
											"districtId",
											(newValue as SelectOption).value,
										)
									}}
									value={districtOptions.find(
										(el) => el.value === values.districtId,
									)}
									options={districtOptions}
									isSearchable
								/>
							</FormControl>
							<Flex gridGap={2}>
								<Box>
									{/* Name */}
									<FormControl>
										<InputLabel label="Taluka Name" />
										<Input
											name="name.en"
											placeholder="Name"
											maxLength={50}
											required
											autoFocus
											value={values.name?.en}
											onChange={handleNameChange}
										/>
										<ErrorMessage
											component={ErrorMessageField}
											name="name.en"
										/>
									</FormControl>
									<Box>
										{nameSuggestions.map((el, i) => (
											<Tag
												colorScheme={"green"}
												backgroundColor={"green.50"}
												variant="outline"
												_hover={{
													backgroundColor: "green.100",
												}}
												cursor="pointer"
												margin={0.5}
												onClick={() => {
													setFieldValue("name.gu", el)
													setNameSuggestions([])
												}}
												key={i}
											>
												{el}
											</Tag>
										))}
									</Box>
								</Box>

								{/* Name Gu */}
								<FormControl>
									<InputLabel label="Name Gujarati" />
									<Input
										name="name.gu"
										placeholder="Name Gujarati"
										maxLength={50}
										required
										value={values.name?.gu}
										onChange={handleChange}
									/>
									<ErrorMessage
										component={ErrorMessageField}
										name="name.gu"
									/>
								</FormControl>
							</Flex>
							<Flex gridGap={2}>
								{/* Is Same as parent */}
								<FormControl>
									<InputLabel label="Same as parent?" />
									<Box>
										<Switch
											isChecked={values.sameAsParent}
											onChange={(val) => {
												setFieldValue(
													"sameAsParent",
													val.target.checked,
												)
											}}
										/>
									</Box>
								</FormControl>
								{/* Is Approved */}
								<FormControl>
									<InputLabel label="Approved?" />
									<Box>
										<Switch
											isChecked={values.isApproved}
											onChange={(val) => {
												setFieldValue(
													"isApproved",
													val.target.checked,
												)
											}}
										/>
									</Box>
								</FormControl>
							</Flex>
						</Stack>
					</DrawerForm>
				)
			}}
		</Formik>
	)
}
